import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { FoodRuntime } from "./food-runtime";
import type { SkinId } from "../config/gameplay-config";
import { gameplayConfig } from "../config/gameplay-config";
import {
  createPlayerMovementState,
  type PlayerMovementState,
  type Vector2,
} from "../simulation/player-movement";
import { headingToward } from "../simulation/player-steering";
import { hasArenaBoundaryContact } from "../simulation/arena-boundary";
import { stepSnakeLocomotion } from "../simulation/snake-locomotion";
import { SimulationClock } from "../simulation/simulation-clock";
import {
  addPlayerGrowth,
  setPlayerGrowth,
  setPlayerLevel,
} from "../simulation/player-growth";
import {
  advancePlayerBodyTrail,
  createPlayerBodyTrail,
  triggerPlayerBodyRipple,
  type PlayerBodyTrailState,
} from "../simulation/player-body-trail";

export interface PlayerPickupFeedback {
  readonly growth: number;
  readonly level: number;
  readonly levelUp: boolean;
  readonly revision: number;
  readonly startedAtMs: number;
}

export interface PlayerRuntimeSnapshot {
  readonly automaticTime: boolean;
  readonly backgrounded: boolean;
  readonly body?: PlayerBodyTrailState | null;
  readonly feedback: PlayerPickupFeedback | null;
  readonly foodCollected: number;
  readonly paused: boolean;
  readonly player: PlayerMovementState | null;
  readonly runId: number;
  readonly simulationTimeMs: number;
}

export interface PlayerDrawableState {
  readonly body: PlayerBodyTrailState;
  readonly player: PlayerMovementState;
}

export interface PlayerRenderSnapshot {
  readonly current: PlayerDrawableState | null;
  readonly fixedStepMs: number;
  readonly previous: PlayerDrawableState | null;
  readonly remainderMs: number;
  readonly simulationTimeMs: number;
}

export interface PlayerRuntimeStabilitySnapshot {
  readonly lastAutomaticDeltaMs: number;
  readonly maximumAutomaticDeltaMs: number;
}

export type PlayerRuntimeListener = (snapshot: PlayerRuntimeSnapshot) => void;
export type PlayerFixedStepListener = (
  stepMilliseconds: number,
  simulationTimeMs: number,
) => void;

const inactiveFoodCollector: Pick<FoodRuntime, "collectOverlapping"> = {
  collectOverlapping: () => [],
};

const defaultStartingLevel = (): number => gameplayConfig.growth.startingLevel;
const defaultMovementSpeed = (): number => gameplayConfig.movement.baseSpeed;
const defaultFoodValue = (): number => 1;

export class PlayerRuntime {
  private automaticTime = true;
  private clock: SimulationClock;
  private discardNextAutomaticFrame = false;
  private feedback: PlayerPickupFeedback | null = null;
  private feedbackRevision = 0;
  private foodCollected = 0;
  private readonly fixedStepCompletionListeners =
    new Set<PlayerFixedStepListener>();
  private readonly fixedStepListeners = new Set<PlayerFixedStepListener>();
  private readonly listeners = new Set<PlayerRuntimeListener>();
  private lastAutomaticDeltaMs = 0;
  private lastAutomaticSampleMs: number | undefined;
  private maximumAutomaticDeltaMs = 0;
  private body: PlayerBodyTrailState | null = null;
  private baseMovementSpeed: number = gameplayConfig.movement.baseSpeed;
  private player: PlayerMovementState | null = null;
  private previousDrawable: PlayerDrawableState | null = null;
  private runId = 0;
  private readonly selectedSkinSource: () => SkinId;
  private steeringDirection: Vector2 | null = null;
  private steeringTarget: Vector2 | null = null;

  public constructor(
    flow: GameFlow,
    selectedSkin: SkinId | (() => SkinId),
    private readonly foodCollector: Pick<
      FoodRuntime,
      "collectOverlapping"
    > = inactiveFoodCollector,
    private readonly startingLevelSource: () => number = defaultStartingLevel,
    private readonly movementSpeedSource: () => number = defaultMovementSpeed,
    private readonly foodValueSource: () => number = defaultFoodValue,
  ) {
    this.selectedSkinSource =
      typeof selectedSkin === "function" ? selectedSkin : () => selectedSkin;
    this.clock = this.createClock();
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public advanceControlled(milliseconds: number): number {
    return this.clock.advanceControlled(milliseconds);
  }

  public advanceFrame(milliseconds: number): number {
    if (!this.automaticTime) {
      this.lastAutomaticDeltaMs = 0;
      return 0;
    }
    if (this.discardNextAutomaticFrame) {
      this.discardNextAutomaticFrame = false;
      this.lastAutomaticDeltaMs = 0;
      return 0;
    }
    const boundedMilliseconds = Math.min(
      milliseconds,
      gameplayConfig.simulation.maximumRealFrameDeltaMs,
    );
    this.lastAutomaticDeltaMs = boundedMilliseconds;
    this.maximumAutomaticDeltaMs = Math.max(
      this.maximumAutomaticDeltaMs,
      boundedMilliseconds,
    );
    return this.clock.advanceControlled(boundedMilliseconds);
  }

  public sampleFrame(sampleMilliseconds: number): number {
    if (
      this.lastAutomaticSampleMs !== undefined &&
      sampleMilliseconds < this.lastAutomaticSampleMs
    ) {
      this.clock.resetRealTimeSample();
    }
    this.lastAutomaticSampleMs = sampleMilliseconds;
    if (!this.automaticTime) {
      this.lastAutomaticDeltaMs = 0;
      this.clock.resetRealTimeSample();
      return 0;
    }
    if (this.discardNextAutomaticFrame) {
      this.discardNextAutomaticFrame = false;
      this.lastAutomaticDeltaMs = 0;
      this.clock.sampleTime(sampleMilliseconds);
      return 0;
    }
    const before = this.clock.getSnapshot();
    const steps = this.clock.sampleTime(sampleMilliseconds);
    const after = this.clock.getSnapshot();
    const elapsedMilliseconds =
      after.elapsedMs +
      after.remainderMs -
      before.elapsedMs -
      before.remainderMs;
    this.lastAutomaticDeltaMs = elapsedMilliseconds;
    this.maximumAutomaticDeltaMs = Math.max(
      this.maximumAutomaticDeltaMs,
      elapsedMilliseconds,
    );
    return steps;
  }

  public applyCombatPosition(position: Vector2): void {
    if (this.player?.alive !== true) {
      throw new Error("Player combat position requires active gameplay");
    }
    this.player = { ...this.player, position: { ...position } };
    this.body =
      this.body === null
        ? createPlayerBodyTrail(this.player, gameplayConfig.playerBody)
        : advancePlayerBodyTrail(
            this.body,
            this.player,
            gameplayConfig.playerBody,
          );
    this.resetRenderInterpolation();
    this.notify();
  }

  public applyBodyCollision(position: Vector2, directionRadians: number): void {
    if (this.player?.alive !== true) {
      throw new Error("Player body collision requires active gameplay");
    }
    this.player = {
      ...this.player,
      directionRadians,
      position: { ...position },
    };
    this.body =
      this.body === null
        ? createPlayerBodyTrail(this.player, gameplayConfig.playerBody)
        : advancePlayerBodyTrail(
            this.body,
            this.player,
            gameplayConfig.playerBody,
          );
    this.steeringTarget = null;
    this.steeringDirection = null;
    this.resetRenderInterpolation();
    this.notify();
  }

  public getSnapshot(): PlayerRuntimeSnapshot {
    const clockSnapshot = this.clock.getSnapshot();
    return {
      automaticTime: this.automaticTime,
      backgrounded: clockSnapshot.backgrounded,
      body:
        this.body === null
          ? null
          : {
              path: this.body.path.map((point) => ({ ...point })),
              rippleStartedAtMs: this.body.rippleStartedAtMs,
              segments: this.body.segments.map((segment) => ({
                position: { ...segment.position },
                radius: segment.radius,
              })),
            },
      feedback: this.feedback === null ? null : { ...this.feedback },
      foodCollected: this.foodCollected,
      paused: clockSnapshot.paused,
      player:
        this.player === null
          ? null
          : {
              ...this.player,
              collision: { ...this.player.collision },
              position: { ...this.player.position },
            },
      runId: this.runId,
      simulationTimeMs: clockSnapshot.elapsedMs,
    };
  }

  public getStabilitySnapshot(): PlayerRuntimeStabilitySnapshot {
    return {
      lastAutomaticDeltaMs: this.lastAutomaticDeltaMs,
      maximumAutomaticDeltaMs: this.maximumAutomaticDeltaMs,
    };
  }

  public getRenderSnapshot(): PlayerRenderSnapshot {
    const clockSnapshot = this.clock.getSnapshot();
    return {
      current: this.copyDrawable(),
      fixedStepMs: gameplayConfig.simulation.fixedStepMs,
      previous: this.cloneDrawable(this.previousDrawable),
      remainderMs: clockSnapshot.remainderMs,
      simulationTimeMs: clockSnapshot.elapsedMs,
    };
  }

  public pause(): void {
    this.releaseSteering();
    this.clock.pause();
    this.resetRenderInterpolation();
  }

  public endRound(): boolean {
    if (this.player === null || !this.clock.stop()) {
      return false;
    }
    this.feedback = null;
    this.steeringTarget = null;
    this.steeringDirection = null;
    this.resetRenderInterpolation();
    this.notify();
    return true;
  }

  public markDefeated(): boolean {
    if (this.player?.alive !== true) {
      return false;
    }
    this.player = { ...this.player, alive: false };
    this.body = createPlayerBodyTrail(this.player, gameplayConfig.playerBody);
    this.feedback = null;
    this.steeringTarget = null;
    this.steeringDirection = null;
    this.resetRenderInterpolation();
    this.notify();
    return true;
  }

  public respawn(position: Vector2, level: number, growth?: number): boolean {
    if (this.player?.alive !== false) {
      return false;
    }
    const resized = setPlayerLevel(this.player, level, gameplayConfig.growth);
    const restored =
      growth === undefined
        ? resized
        : setPlayerGrowth(resized, growth, gameplayConfig.growth);
    this.player = {
      ...restored,
      alive: true,
      directionRadians: gameplayConfig.movement.initialHeadingRadians,
      position: { ...position },
    };
    this.body = createPlayerBodyTrail(this.player, gameplayConfig.playerBody);
    this.feedback = null;
    this.steeringTarget = null;
    this.steeringDirection = null;
    this.resetRenderInterpolation();
    this.notify();
    return true;
  }

  public setSpeedMultiplier(multiplier: number): void {
    if (!Number.isFinite(multiplier) || multiplier <= 0) {
      throw new RangeError("Player speed multiplier must be positive");
    }
    if (this.player === null) {
      return;
    }
    this.player = {
      ...this.player,
      speed: this.baseMovementSpeed * multiplier,
    };
    this.notify();
  }

  public setLevel(level: number): void {
    if (this.player?.alive !== true) {
      throw new Error("Player level can only change during gameplay");
    }
    const previousLevel = this.player.level;
    this.player = setPlayerLevel(this.player, level, gameplayConfig.growth);
    const body =
      this.body === null
        ? createPlayerBodyTrail(this.player, gameplayConfig.playerBody)
        : advancePlayerBodyTrail(
            this.body,
            this.player,
            gameplayConfig.playerBody,
          );
    this.body =
      level > previousLevel
        ? triggerPlayerBodyRipple(body, this.clock.getSnapshot().elapsedMs)
        : body;
    this.feedback = null;
    this.resetRenderInterpolation();
    this.notify();
  }

  public setGrowth(growth: number, ripple: boolean): void {
    if (this.player?.alive !== true) {
      throw new Error("Player growth can only change during gameplay");
    }
    this.player = setPlayerGrowth(this.player, growth, gameplayConfig.growth);
    const body =
      this.body === null
        ? createPlayerBodyTrail(this.player, gameplayConfig.playerBody)
        : advancePlayerBodyTrail(
            this.body,
            this.player,
            gameplayConfig.playerBody,
          );
    this.body = ripple
      ? triggerPlayerBodyRipple(body, this.clock.getSnapshot().elapsedMs)
      : body;
    this.feedback = null;
    this.resetRenderInterpolation();
    this.notify();
  }

  public resume(): void {
    const wasPaused = this.clock.getSnapshot().paused;
    this.clock.resume();
    if (wasPaused) {
      this.discardNextAutomaticFrame = true;
    }
  }

  public background(): void {
    this.releaseSteering();
    this.clock.background();
    this.resetRenderInterpolation();
  }

  public foreground(): void {
    const wasBackgrounded = this.clock.getSnapshot().backgrounded;
    this.clock.foreground();
    if (wasBackgrounded) {
      this.discardNextAutomaticFrame = true;
    }
  }

  public releaseSteering(): void {
    this.steeringDirection = null;
    this.steeringTarget = null;
  }

  public resetAutomaticFrameTiming(): void {
    this.lastAutomaticDeltaMs = 0;
    this.clock.resetRealTimeSample();
  }

  public steerDirection(direction: Vector2): void {
    const clockSnapshot = this.clock.getSnapshot();
    if (
      this.player !== null &&
      this.player.alive &&
      !clockSnapshot.backgrounded &&
      !clockSnapshot.paused
    ) {
      this.steeringDirection = { ...direction };
      this.steeringTarget = null;
    }
  }

  public setAutomaticTime(enabled: boolean): void {
    this.automaticTime = enabled;
    this.clock.resetRealTimeSample();
  }

  public resetRenderInterpolation(): void {
    this.previousDrawable = this.copyDrawable();
  }

  public steerToward(target: Vector2): void {
    const clockSnapshot = this.clock.getSnapshot();
    if (
      this.player !== null &&
      this.player.alive &&
      !clockSnapshot.backgrounded &&
      !clockSnapshot.paused
    ) {
      this.steeringDirection = null;
      this.steeringTarget = { ...target };
    }
  }

  public subscribe(listener: PlayerRuntimeListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  public subscribeFixedStep(listener: PlayerFixedStepListener): () => void {
    this.fixedStepListeners.add(listener);
    return () => {
      this.fixedStepListeners.delete(listener);
    };
  }

  public subscribeFixedStepCompletion(
    listener: PlayerFixedStepListener,
  ): () => void {
    this.fixedStepCompletionListeners.add(listener);
    return () => {
      this.fixedStepCompletionListeners.delete(listener);
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    this.discardNextAutomaticFrame = false;
    this.lastAutomaticDeltaMs = 0;
    this.maximumAutomaticDeltaMs = 0;
    if (snapshot.scene === "results") {
      return;
    }
    if (snapshot.scene === "home") {
      this.player = null;
      this.body = null;
      this.feedback = null;
      this.foodCollected = 0;
      this.steeringDirection = null;
      this.steeringTarget = null;
      this.clock = this.createClock();
      this.resetRenderInterpolation();
      this.notify();
      return;
    }
    this.clock = this.createClock();
    this.steeringDirection = null;
    this.steeringTarget = null;
    this.runId += 1;
    this.feedback = null;
    this.foodCollected = 0;
    const level = this.startingLevelSource();
    this.baseMovementSpeed = this.movementSpeedSource();
    this.player = setPlayerLevel(
      createPlayerMovementState({
        directionRadians: gameplayConfig.movement.initialHeadingRadians,
        level: gameplayConfig.growth.startingLevel,
        position: gameplayConfig.movement.initialPosition,
        selectedSkinId: this.selectedSkinSource(),
        size: gameplayConfig.growth.startingRadius,
        speed: this.baseMovementSpeed,
      }),
      level,
      gameplayConfig.growth,
    );
    this.body = createPlayerBodyTrail(this.player, gameplayConfig.playerBody);
    this.resetRenderInterpolation();
    this.notify();
  }

  private collectFood(player: PlayerMovementState): boolean {
    const collected = this.foodCollector.collectOverlapping(
      player.position,
      player.collision.radius,
    );
    if (collected.length === 0) {
      return false;
    }
    const previousLevel = player.level;
    const growth =
      collected.reduce((total, food) => total + food.growth, 0) *
      this.foodValueSource();
    this.player = addPlayerGrowth(player, growth, gameplayConfig.growth);
    this.foodCollected += collected.length;
    this.feedbackRevision += 1;
    this.feedback = {
      growth,
      level: this.player.level,
      levelUp: this.player.level > previousLevel,
      revision: this.feedbackRevision,
      startedAtMs: this.clock.getSnapshot().elapsedMs,
    };
    return true;
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getSnapshot());
    }
  }

  private cloneBody(body: PlayerBodyTrailState): PlayerBodyTrailState {
    return {
      path: body.path.map((point) => ({ ...point })),
      rippleStartedAtMs: body.rippleStartedAtMs,
      segments: body.segments.map((segment) => ({
        position: { ...segment.position },
        radius: segment.radius,
      })),
    };
  }

  private cloneDrawable(
    drawable: PlayerDrawableState | null,
  ): PlayerDrawableState | null {
    return drawable === null
      ? null
      : {
          body: this.cloneBody(drawable.body),
          player: {
            ...drawable.player,
            collision: { ...drawable.player.collision },
            position: { ...drawable.player.position },
          },
        };
  }

  private copyDrawable(): PlayerDrawableState | null {
    return this.player === null || this.body === null
      ? null
      : this.cloneDrawable({ body: this.body, player: this.player });
  }

  private createClock(): SimulationClock {
    return new SimulationClock({
      config: gameplayConfig.simulation,
      onFixedStep: (stepMilliseconds) => {
        this.previousDrawable = this.copyDrawable();
        const feedback = this.feedback;
        if (
          feedback !== null &&
          this.clock.getSnapshot().elapsedMs - feedback.startedAtMs >=
            gameplayConfig.playerStatus.feedbackDurationMs
        ) {
          this.feedback = null;
          this.notify();
        }
        if (this.player?.alive === true) {
          const targetHeading =
            this.steeringDirection === null
              ? this.steeringTarget === null
                ? this.player.directionRadians
                : headingToward(
                    this.player.position,
                    this.steeringTarget,
                    this.player.directionRadians,
                  )
              : headingToward(
                  { x: 0, y: 0 },
                  this.steeringDirection,
                  this.player.directionRadians,
                );
          const movement = stepSnakeLocomotion(
            this.player,
            targetHeading,
            gameplayConfig.movement.turnRateRadiansPerSecond,
            stepMilliseconds,
            gameplayConfig.arena,
          );
          this.player = movement.snake;
          if (hasArenaBoundaryContact(movement.contact)) {
            this.steeringDirection = null;
            this.steeringTarget = null;
          }
          const collectedFood = this.collectFood(this.player);
          const body =
            this.body === null
              ? createPlayerBodyTrail(this.player, gameplayConfig.playerBody)
              : advancePlayerBodyTrail(
                  this.body,
                  this.player,
                  gameplayConfig.playerBody,
                );
          this.body = collectedFood
            ? triggerPlayerBodyRipple(body, this.clock.getSnapshot().elapsedMs)
            : body;
          if (collectedFood) {
            this.notify();
          }
        }
        const simulationTimeMs = this.clock.getSnapshot().elapsedMs;
        for (const listener of this.fixedStepListeners) {
          listener(stepMilliseconds, simulationTimeMs);
        }
        for (const listener of this.fixedStepCompletionListeners) {
          listener(stepMilliseconds, simulationTimeMs);
        }
      },
      timeSource: { now: () => performance.now() },
    });
  }
}
